import { Check, Crown, Sparkles } from 'lucide-react';
import { Link } from 'wouter';

const plans = [
  {
    name: 'Free',
    price: 'Free',
    note: 'Try the experience first',
    features: ['3 successful AI generations', 'Basic turban styles', 'Basic colour matching', 'Standard image quality', 'Share your looks'],
    action: 'Start for free',
  },
  {
    name: 'Pro Monthly',
    price: 'In-app price',
    note: 'Purchase securely in the Turbans AI iOS app. Renews monthly until cancelled; no free trial at launch.',
    features: ['More try-ons after the free allowance', 'Store-managed monthly access', 'Restore purchases on supported devices', 'Cancel through your App Store settings'],
    action: 'Available in the iOS app',
    featured: true,
  },
  {
    name: 'Pro Annual',
    price: 'In-app price',
    note: 'Purchase securely in the Turbans AI iOS app. Renews yearly until cancelled; no free trial at launch.',
    features: ['More try-ons after the free allowance', 'Store-managed annual access', 'Restore purchases on supported devices', 'Cancel through your App Store settings'],
    action: 'Available in the iOS app',
  },
];

export default function Pricing() {
  return (
    <div className="page-enter">
      <section className="border-b border-border/70 bg-card/50">
        <div className="mx-auto max-w-[1100px] px-5 py-16 text-center sm:px-8 lg:px-12">
          <p className="mono-font text-[10px] uppercase tracking-[.18em] text-primary">Choose your access</p>
          <h1 className="display-font mx-auto mt-5 max-w-3xl text-5xl leading-[.92] tracking-[-.05em] sm:text-7xl">
            Find your look for less than the cost of <em className="not-italic text-primary">one turban.</em>
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-base leading-7 text-muted-foreground">
            Start with three successful AI generations. Corrections also create a new AI generation; secure colour-only changes do not use the free allowance.
          </p>
        </div>
      </section>
      <section className="mx-auto grid max-w-[1100px] gap-4 px-5 py-12 sm:grid-cols-3 sm:px-8 lg:px-12 lg:py-20">
        {plans.map((plan) => (
          <article
            key={plan.name}
            className={`relative rounded-2xl border p-6 sm:p-8 ${plan.featured ? 'border-primary bg-primary text-primary-foreground shadow-xl lg:-translate-y-3' : 'border-border bg-card'}`}
          >
            {plan.featured && (
              <span className="mono-font absolute -top-3 left-6 rounded-full bg-secondary px-3 py-1 text-[10px] font-bold uppercase tracking-[.12em] text-secondary-foreground">
                Monthly
              </span>
            )}
            <div className="flex items-center justify-between">
              <h2 className="display-font text-3xl">{plan.name}</h2>
              {plan.featured ? <Crown size={20} className="text-secondary" /> : <Sparkles size={20} className="text-muted-foreground" />}
            </div>
            <p className={`mt-3 min-h-[72px] text-sm leading-6 ${plan.featured ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{plan.note}</p>
            <p className="display-font mt-8 text-4xl">{plan.price}</p>
            <ul className="mt-8 space-y-3 border-t border-current/10 pt-6">
              {plan.features.map((item) => (
                <li key={item} className={`flex items-center gap-3 text-sm ${plan.featured ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  <Check size={15} className={plan.featured ? 'text-secondary' : 'text-primary'} />
                  {item}
                </li>
              ))}
            </ul>
            {plan.name === 'Free' ? (
              <Link href="/try" className="mt-9 flex items-center justify-center rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition hover:shadow-lg">
                {plan.action}
              </Link>
            ) : (
              <span
                className={`mt-9 flex items-center justify-center rounded-xl px-4 py-3 text-center text-sm font-bold ${plan.featured ? 'bg-secondary text-secondary-foreground' : 'bg-primary text-primary-foreground'}`}
                aria-label={`${plan.name} is available for purchase in the iOS app`}
              >
                {plan.action}
              </span>
            )}
          </article>
        ))}
      </section>
      <p className="mx-auto max-w-2xl px-5 pb-16 text-center text-xs leading-5 text-muted-foreground">
        Pro Monthly and Pro Annual renew until cancelled and have no free trial. Prices are set and localised by Apple App Store; the native iOS app shows the actual currency and store price before purchase.
      </p>
    </div>
  );
}